/**
 * QuestionTypeSelector Component
 * Renders AI content type options for selection.
 * Receives options from parent (AIContentGenerator) instead of fetching internally.
 */

import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { getQuestionTypeLabel, getPromptLocale } from '@ai-content-hub/content-prompt';
import type { QuestionTypeSelectorProps, QuestionTypeOption, AIGeneratorColors } from './AIContentGenerator.types';
import { DEFAULT_COLORS } from './AIContentGenerator.types';
import { useAIGeneratorTranslations } from './i18n';

function typeLabel(slug: string, fallback?: string): string {
  // EAA-2031: getQuestionTypeLabel echoes an unknown slug back, which is truthy, so the
  // row's display name was never used and the raw slug leaked into the dropdown.
  const label = getQuestionTypeLabel(slug, getPromptLocale());
  return label !== slug ? label : fallback || slug;
}

export const QuestionTypeSelector: React.FC<QuestionTypeSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  placeholder,
  colors: colorsProp,
  className,
  style: styleProp,
  options: optionsProp = [],
  loading: loadingProp = false,
  error: errorProp = null,
}) => {
  const colors: AIGeneratorColors = useMemo(
    () => ({ ...DEFAULT_COLORS, ...colorsProp }),
    [colorsProp]
  );

  const t = useAIGeneratorTranslations();

  const [isOpen, setIsOpen] = useState(false);

  const selectedOption = useMemo(
    () => optionsProp.find((opt) => opt.slug === value),
    [optionsProp, value]
  );

  const handleSelect = useCallback((option: QuestionTypeOption) => {
    onChange(option.slug);
    setIsOpen(false);
  }, [onChange]);

  const handleToggle = useCallback(() => {
    if (!disabled && !loadingProp) {
      setIsOpen((prev) => !prev);
    }
  }, [disabled, loadingProp]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.question-type-selector')) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const displayText = loadingProp
    ? t.loading
    : errorProp
    ? t.errorLoadingTypes
    : selectedOption
    ? typeLabel(selectedOption.slug, selectedOption.name)
    : placeholder || t.questionTypePlaceholder;

  return (
    <div
      className={`question-type-selector ${className || ''}`}
      style={{ position: 'relative', width: 240, ...styleProp }}
    >
      <button
        type="button"
        className="question-type-selector-button"
        onClick={handleToggle}
        disabled={disabled || loadingProp}
        style={{
          width: '100%',
          padding: '8px 16px',
          border: `1px solid ${colors.border}`,
          borderRadius: 6,
          background: disabled ? colors.surface : colors.background,
          color: selectedOption ? colors.text : colors.textMuted,
          fontSize: 14,
          textAlign: 'left',
          cursor: disabled || loadingProp ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          transition: 'all 200ms',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {displayText}
        </span>

        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          style={{
            flexShrink: 0,
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 200ms',
          }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {isOpen && optionsProp.length > 0 && (
        <div
          className="question-type-selector-dropdown"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 8,
            background: colors.background,
            border: `1px solid ${colors.border}`,
            borderRadius: 6,
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            maxHeight: 256,
            overflowY: 'auto',
            zIndex: 50,
          }}
        >
          {optionsProp.map((option) => (
            <button
              key={option.id}
              type="button"
              className="question-type-selector-option"
              onClick={() => handleSelect(option)}
              style={{
                width: '100%',
                padding: '8px 16px',
                border: 'none',
                background: option.slug === value ? `${colors.primary}10` : 'transparent',
                color: colors.text,
                fontSize: 14,
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                transition: 'background 200ms',
              }}
              onMouseEnter={(e) => {
                if (option.slug !== value) {
                  e.currentTarget.style.background = colors.surface;
                }
              }}
              onMouseLeave={(e) => {
                if (option.slug !== value) {
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              <span style={{ fontWeight: option.slug === value ? 500 : 400 }}>
                {typeLabel(option.slug, option.name)}
              </span>
              {option.description && (
                <span
                  style={{
                    fontSize: 12,
                    color: colors.textMuted,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {option.description}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {errorProp && (
        <div
          className="question-type-selector-error"
          style={{
            marginTop: 8,
            fontSize: 12,
            color: colors.error,
          }}
        >
          {errorProp}
        </div>
      )}
    </div>
  );
};

export default QuestionTypeSelector;
